/**
 * Finding the person on the telephone from the number they are ringing on.
 *
 * Ported from legacy `new-order.tsx:213` (`lookupByPhone`) and `:246` (`handlePhoneChange`). The
 * endpoint is `GET /customers/lookup`, which AGOS already serves — its own comment reads *"A waiter may
 * use this: it is how a phone order gets an address without asking for it again."*
 *
 * **This is not `BLOCKED.md` #11.** That entry is about `GET /customers/loyalty-check`, which is
 * unauthenticated and hands anybody who knows an e-mail that person's points and personal discount.
 * This one is `@RequireAuth({"admin", "main_admin", "waiter"})` — the caller is staff taking an order,
 * which is the case the endpoint exists for. Recording that here because the two are one keystroke
 * apart in the API and confusing them is how the open one would get used.
 */

/**
 * Whether a number is worth asking about yet — legacy's `trimmed.length < 9`.
 *
 * Slovak mobile numbers are nine digits after the leading zero, so anything shorter cannot identify
 * one person. Asking earlier would fire a request per keystroke and return whoever happens to share a
 * prefix, which is worse than not asking: it would auto-fill a stranger's name.
 */
export function worthLookingUp(phone: string): boolean {
  return phone.trim().replace(/\s+/g, '').length >= 9
}

export interface CustomerMatch {
  name: string
  email: string
  /** Distinct delivery addresses this number has ordered to, newest first. */
  pastAddresses: string[]
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/** `GET /customers/lookup`'s untyped body into the two things the builder fills. */
export function matchOf(payload: unknown): CustomerMatch | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>

  const addresses = Array.isArray(row.pastAddresses)
    ? row.pastAddresses.map(text).filter((address) => address !== '')
    : []

  const customer =
    row.found === true && typeof row.customer === 'object' && row.customer !== null
      ? (row.customer as Record<string, unknown>)
      : null

  // A number with no customer row but past orders is still a match: the addresses are the useful half,
  // and legacy fills them whether or not `found` is true.
  if (customer === null && addresses.length === 0) return null
  return {
    name: customer === null ? '' : text(customer.name),
    email: customer === null ? '' : text(customer.email),
    pastAddresses: addresses,
  }
}

export interface CustomerFields {
  name: string
  email: string
  address: string
}

/**
 * What the match changes, and — more to the point — what it does not.
 *
 * **Nothing already typed is overwritten.** Legacy guards each field with `&& !customerName`, and it is
 * right to: the person taking the order has the customer on the telephone and the database has whoever
 * last used that number. A shared household line, a reassigned mobile, or simply a correction typed a
 * second before the answer arrived — in every one of those the typed value is the current truth.
 *
 * The address is filled **only when there is exactly one**. More than one and legacy offers the list
 * instead, because picking the newest silently would deliver to the flat somebody moved out of.
 */
export function filledFrom(match: CustomerMatch, current: CustomerFields): CustomerFields {
  const onlyAddress = match.pastAddresses.length === 1 ? match.pastAddresses[0] ?? '' : ''
  return {
    name: current.name === '' ? match.name : current.name,
    email: current.email === '' ? match.email : current.email,
    address: current.address === '' ? onlyAddress : current.address,
  }
}

/** What the *Doplnené z databázy* badge names, or `null` when nothing was filled in. */
export function autofilledLabel(match: CustomerMatch, fallback: string): string | null {
  if (match.name !== '') return match.name
  if (match.email !== '') return match.email
  return match.pastAddresses.length > 0 ? fallback : null
}
